import React, {useCallback, useRef, useState} from 'react';
import {ActivityIndicator, Pressable, TextInput, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {
  IngestPart,
  readVideoDuration,
  uploadVideo,
  useIngestCourse,
} from '../../../Hooks/LmsCourseApi/useCourseUpload';

import CourseButton from './CourseButton';
import {CARD_SHADOW, F} from '../courseTheme';

type Props = {
  /** Called with the recording id to follow once generation has been queued. */
  onStarted: (videoCourseId: number, note: string) => void;
};

type PartStatus = 'ready' | 'uploading' | 'uploaded' | 'failed';

type Part = {
  id: string;
  file: File;
  title: string;
  transcript: string;
  transcriptName: string;
  durationSec: number;
  progress: number;
  status: PartStatus;
  key?: string;
  error?: string;
  /** The file was already in the bucket; nothing was transferred. */
  reused?: boolean;
  reusedAt?: string;
};

const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.mkv,.webm';
const TRANSCRIPT_ACCEPT = '.vtt,.srt,.txt,text/vtt,text/plain';

/**
 * Open a file dialog and resolve with what was chosen.
 *
 * Resolves empty when the dialog is dismissed rather than hanging: `change`
 * does not fire on cancel in any browser, so a promise waiting only on it
 * would never settle and the button would stay disabled for good. `cancel`
 * covers current browsers; the focus fallback covers the rest.
 */
function pickFiles(accept: string, multiple: boolean): Promise<File[]> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;
    input.style.display = 'none';

    let settled = false;
    const finish = (files: File[]) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('focus', onFocus);
      input.remove();
      resolve(files);
    };
    const onFocus = () => {
      // Runs when the dialog closes, before `change` on a real selection.
      setTimeout(() => finish(Array.from(input.files || [])), 400);
    };

    input.onchange = () => finish(Array.from(input.files || []));
    input.oncancel = () => finish([]);
    window.addEventListener('focus', onFocus);

    document.body.appendChild(input);
    input.click();
  });
}

const readText = (file: File): Promise<string> =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsText(file);
  });

const humanSize = (bytes: number) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const humanDuration = (seconds: number) => {
  if (!seconds) return 'length unknown';
  const mins = Math.floor(seconds / 60);
  return mins >= 60
    ? `${Math.floor(mins / 60)}h ${mins % 60}m`
    : `${mins}m ${seconds % 60}s`;
};

let nextId = 0;

export default function UploadCourseCard({onStarted}: Props) {
  const [title, setTitle] = useState('');
  const [parts, setParts] = useState<Part[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelled = useRef<AbortController | null>(null);

  const ingest = useIngestCourse();

  const patch = useCallback((id: string, changes: Partial<Part>) => {
    setParts(current =>
      current.map(part => (part.id === id ? {...part, ...changes} : part)),
    );
  }, []);

  const addVideos = useCallback(async () => {
    const files = await pickFiles(VIDEO_ACCEPT, true);
    if (!files.length) return;
    const added: Part[] = files.map(file => ({
      id: `p${nextId++}`,
      file,
      title: file.name.replace(/\.[^.]+$/, ''),
      transcript: '',
      transcriptName: '',
      durationSec: 0,
      progress: 0,
      status: 'ready' as PartStatus,
    }));
    setParts(current => [...current, ...added]);
    setError(null);

    // Read durations after the rows are on screen — metadata parsing on a
    // multi-gigabyte file is not instant, and the list should not wait on it.
    added.forEach(async part => {
      const seconds = await readVideoDuration(part.file);
      patch(part.id, {durationSec: seconds});
    });
  }, [patch]);

  const attachTranscript = useCallback(
    async (id: string) => {
      const [file] = await pickFiles(TRANSCRIPT_ACCEPT, false);
      if (!file) return;
      patch(id, {transcript: await readText(file), transcriptName: file.name});
    },
    [patch],
  );

  const move = useCallback((index: number, by: number) => {
    setParts(current => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setParts(current => current.filter(part => part.id !== id));
  }, []);

  const submit = useCallback(async () => {
    if (!parts.length || busy) return;
    setBusy(true);
    setError(null);
    const controller = new AbortController();
    cancelled.current = controller;

    try {
      // Sequential, not parallel. Several multi-gigabyte uploads at once split
      // the same uplink, so everything finishes later and each progress bar
      // crawls — and a failure midway leaves more half-finished objects to
      // clean up.
      const uploaded: IngestPart[] = [];
      for (const part of parts) {
        // A retry after a partial failure must not pay to upload again what
        // already landed.
        if (part.key) {
          uploaded.push({
            key: part.key,
            title: part.title,
            transcript: part.transcript,
            duration_sec: part.durationSec,
          });
          continue;
        }

        patch(part.id, {status: 'uploading', progress: 0, error: undefined});
        try {
          // Presigned first, proxied through our backend if the bucket has no
          // CORS rule. Which route was taken is not this component's business.
          const {key, reused, uploadedAt} = await uploadVideo(
            part.file,
            fraction => patch(part.id, {progress: fraction}),
            controller.signal,
          );
          patch(part.id, {
            status: 'uploaded', progress: 1, key,
            // Shown rather than silent: reuse is decided from a fingerprint
            // of the file's ends, so the person should see which file is
            // being used instead of discovering it in the finished course.
            reused,
            reusedAt: uploadedAt ?? undefined,
          });
          uploaded.push({
            key,
            title: part.title,
            transcript: part.transcript,
            duration_sec: part.durationSec,
          });
        } catch (uploadError: any) {
          const detail =
            uploadError?.response?.data?.error ??
            uploadError?.message ??
            'Upload failed.';
          patch(part.id, {status: 'failed', error: detail});
          throw new Error(`${part.title}: ${detail}`);
        }
      }

      const job = await ingest.mutateAsync({
        title: title.trim() || parts[0].title,
        parts: uploaded,
      });

      const notes: string[] = [];
      if (job.transcripts_unusable?.length) {
        notes.push(
          `Transcript for part ${job.transcripts_unusable.join(', ')} had no ` +
            `timings and could not be used.`,
        );
      }
      if (job.transcripts_pending?.length) {
        notes.push(
          `Part ${job.transcripts_pending.join(', ')} will be transcribed ` +
            `automatically, which adds a few minutes per hour of video.`,
        );
      }
      onStarted(job.video_course_id, notes.join(' '));
    } catch (submitError: any) {
      setError(
        submitError?.response?.data?.error ??
          submitError?.message ??
          'Could not start the course.',
      );
    } finally {
      cancelled.current = null;
      setBusy(false);
    }
  }, [busy, ingest, onStarted, parts, patch, title]);

  const totalSeconds = parts.reduce((sum, part) => sum + part.durationSec, 0);

  return (
    <View
      style={[
        {
          backgroundColor: F.white,
          borderRadius: F.radius.card,
          padding: 24,
          gap: 12,
          maxWidth: 720,
        },
        CARD_SHADOW,
      ]}>
      <CCText style={{fontSize: 22, fontWeight: '600', color: F.brownDark}}>
        Upload videos and build a course
      </CCText>
      <CCText style={{fontSize: 15, fontWeight: '500', color: F.brown, lineHeight: 24}}>
        Add one or more videos in the order they should be watched. They become
        a single course, numbered straight through. Attach a transcript to each
        if you have one — anything without a transcript is transcribed
        automatically.
      </CCText>

      <TextInput
        value={title}
        onChangeText={setTitle}
        placeholder="Course title"
        accessibilityLabel="Course title"
        style={{
          borderWidth: 1,
          borderColor: 'rgba(38,35,34,0.2)',
          borderRadius: 8,
          padding: 12,
          fontSize: 14,
        }}
      />

      {parts.map((part, index) => (
        <View
          key={part.id}
          style={{
            borderWidth: 1,
            borderColor: 'rgba(38,35,34,0.15)',
            borderRadius: 8,
            padding: 12,
            gap: 8,
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
            <CCText style={{fontSize: 13, fontWeight: '700', color: F.teal}}>
              {`Part ${index + 1}`}
            </CCText>
            <TextInput
              value={part.title}
              onChangeText={value => patch(part.id, {title: value})}
              accessibilityLabel={`Title for part ${index + 1}`}
              style={{
                flex: 1,
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.2)',
                borderRadius: 6,
                paddingHorizontal: 8,
                paddingVertical: 6,
                fontSize: 13,
              }}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Move part ${index + 1} up`}
              disabled={index === 0 || busy}
              onPress={() => move(index, -1)}>
              <Ionicons
                name="arrow-up"
                size={18}
                color={index === 0 || busy ? 'rgba(38,35,34,0.25)' : F.brown}
              />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Move part ${index + 1} down`}
              disabled={index === parts.length - 1 || busy}
              onPress={() => move(index, 1)}>
              <Ionicons
                name="arrow-down"
                size={18}
                color={
                  index === parts.length - 1 || busy
                    ? 'rgba(38,35,34,0.25)'
                    : F.brown
                }
              />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Remove part ${index + 1}`}
              disabled={busy}
              onPress={() => remove(part.id)}>
              <Ionicons
                name="close"
                size={18}
                color={busy ? 'rgba(38,35,34,0.25)' : F.wrongFg}
              />
            </Pressable>
          </View>

          <CCText style={{fontSize: 12, fontWeight: '500', color: F.brown}}>
            {`${part.file.name} · ${humanSize(part.file.size)} · ${humanDuration(
              part.durationSec,
            )}`}
          </CCText>

          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10}}>
            <Pressable
              accessibilityRole="button"
              disabled={busy}
              onPress={() => attachTranscript(part.id)}>
              <CCText
                style={{
                  fontSize: 13,
                  fontWeight: '600',
                  color: busy ? 'rgba(38,35,34,0.4)' : F.teal,
                }}>
                {part.transcriptName ? 'Replace transcript' : 'Attach transcript'}
              </CCText>
            </Pressable>
            <CCText style={{fontSize: 12, fontWeight: '500', color: F.brown, flex: 1}}>
              {part.transcriptName || 'No transcript — will be transcribed'}
            </CCText>
          </View>

          {part.status === 'uploading' || part.status === 'uploaded' ? (
            <View style={{gap: 4}}>
              <View
                style={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(38,35,34,0.1)',
                  overflow: 'hidden',
                }}>
                <View
                  style={{
                    width: `${Math.round(part.progress * 100)}%`,
                    height: 6,
                    backgroundColor: F.teal,
                  }}
                />
              </View>
              <CCText style={{fontSize: 11, fontWeight: '600', color: F.brown}}>
                {part.status !== 'uploaded'
                  ? `Uploading ${Math.round(part.progress * 100)}%`
                  : part.reused
                  ? `Already uploaded${
                      part.reusedAt
                        ? ` on ${new Date(part.reusedAt).toLocaleDateString()}`
                        : ''
                    } — reusing it, nothing sent`
                  : 'Uploaded'}
              </CCText>
            </View>
          ) : null}

          {part.error ? (
            <CCText style={{fontSize: 12, fontWeight: '600', color: F.wrongFg}}>
              {part.error}
            </CCText>
          ) : null}
        </View>
      ))}

      {parts.length ? (
        <CCText style={{fontSize: 12, fontWeight: '600', color: F.brown}}>
          {`${parts.length} video${parts.length > 1 ? 's' : ''} · ${humanDuration(
            totalSeconds,
          )} total`}
        </CCText>
      ) : null}

      <View style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
        <CourseButton
          label={parts.length ? 'Add more videos' : 'Choose videos'}
          variant="review"
          disabled={busy}
          onPress={addVideos}
        />
        <CourseButton
          label={busy ? 'Uploading…' : 'Build course'}
          disabled={!parts.length || busy}
          onPress={submit}
        />
        {busy ? <ActivityIndicator color={F.teal} /> : null}
      </View>

      {error ? (
        <CCText style={{fontSize: 14, fontWeight: '600', color: F.wrongFg}}>
          {error}
        </CCText>
      ) : null}
    </View>
  );
}
